import { useState, useEffect } from 'react'
import { DevicePhoneMobileIcon, UserCircleIcon, CheckIcon, XMarkIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import CustomerService from '@/services/CustomerService'
import { useNotifications } from '@/hooks/useNotifications'
import { useTranslation } from 'react-i18next'

export default function CustomerDetailsCard({ customerId, customerDetails, onCustomerNumberUpdate, onSalesRepNumberUpdate }) {
	const { t } = useTranslation()
	const [isEditingPhone, setIsEditingPhone] = useState(false)
	const [isEditingSalesRep, setIsEditingSalesRep] = useState(false)
	const [phoneNumber, setPhoneNumber] = useState(customerDetails?.tel || '')
	const [salesRepNumber, setSalesRepNumber] = useState(customerDetails?.salesRepNumber || '')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [message, setMessage] = useState(null)

	useNotifications({
		error,
		message,
		clearError: () => setError(null),
		clearMessage: () => setMessage(null),
	})

	// Synchronize phone number state when customerDetails changes
	// This ensures the component stays in sync with parent state updates
	useEffect(() => {
		if (!isEditingPhone) {
			setPhoneNumber(customerDetails?.tel || '')
		}
	}, [customerDetails?.tel, isEditingPhone])

	// Synchronize sales rep number state when customerDetails changes
	// This ensures the component stays in sync with parent state updates
	useEffect(() => {
		if (!isEditingSalesRep) {
			setSalesRepNumber(customerDetails?.salesRepNumber || '')
		}
	}, [customerDetails?.salesRepNumber, isEditingSalesRep])

	const handleEditPhone = () => {
		setIsEditingPhone(true)
		setPhoneNumber(customerDetails?.tel || '')
	}

	const handleCancelPhone = () => {
		setIsEditingPhone(false)
		setPhoneNumber(customerDetails?.tel || '')
		setError(null)
	}

	const handleSavePhone = async () => {
		if (!customerId || !customerDetails?._id) return

		setSaving(true)
		setError(null)
		setMessage(t('customers.contactRecord.customerCard.phoneUpdated'))
		setIsEditingPhone(false)
		onCustomerNumberUpdate(phoneNumber)
		setSaving(false)
	}

	const handleEditSalesRep = () => {
		setIsEditingSalesRep(true)
		setSalesRepNumber(customerDetails?.salesRepNumber || '')
	}

	const handleCancelSalesRep = () => {
		setIsEditingSalesRep(false)
		setSalesRepNumber(customerDetails?.salesRepNumber || '')
		setError(null)
	}

	const handleSaveSalesRep = () => {
		setIsEditingSalesRep(false)
		onSalesRepNumberUpdate(salesRepNumber)
	}

	if (!customerDetails) {
		return null
	}

	const customer = customerDetails
	const person = { ...customer }
	person.imageUrl = '/business-man.png'
	if (person.title) person.imageUrl = person.title === 'Mr' ? '/business-man.png' : '/business-woman.png'
	if (person.sex) person.imageUrl = person.sex === 'male' ? '/business-man.png' : '/business-woman.png'

	return (
		<div className="rounded-md border border-slate-200 bg-white shadow-sm">
			{/* Header */}
			<div className="flex w-full items-center justify-between space-x-6 p-6 py-4">
				<div className="flex-1 truncate">
					<div className="flex items-center space-x-3">
						<h2 className="truncate text-lg text-gray-900 font-semibold">{person.name}</h2>
					</div>
				</div>
				<div className="flex items-center justify-end">
					<img
						alt=""
						src={person.imageUrl}
						className="size-12 shrink-0 rounded-full bg-gray-300 outline -outline-offset-1 outline-black/5"
					/>
				</div>
			</div>

			{/* Customer Details */}
			<div className="px-6 pb-6">
				{/* Full Name */}
				<div className="py-2 flex items-start gap-3">
					<UserCircleIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3">
						<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none">{t('customers.contactRecord.customerCard.fullName')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{(person.title ? `${person.title} ${person.name}` : person.name) || '—'}
						</dd>
					</div>
				</div>

				{/* Phone Number - Editable */}
				<div className="py-2 flex items-start gap-3">
					<DevicePhoneMobileIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none">{t('customers.contactRecord.customerCard.phone')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingPhone ? (
								<div className="flex items-center gap-2">
									<input
										type="tel"
										value={phoneNumber}
										onChange={(e) => setPhoneNumber(e.target.value)}
										placeholder={t('customers.form.placeholders.phone')}
										disabled={saving}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSavePhone}
										disabled={saving}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelPhone}
										disabled={saving}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{phoneNumber || '—'}</span>
									<button
										type="button"
										onClick={handleEditPhone}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('customers.contactRecord.customerCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>

				{/* Sales Rep Number - Editable */}
				<div className="py-2 flex items-start gap-3">
					<IdentificationIcon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
					<div className="w-full sm:flex sm:items-start sm:gap-3 flex-1">
						<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none">{t('customers.contactRecord.customerCard.salesRepNumber')}</dt>
						<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">
							{isEditingSalesRep ? (
								<div className="flex items-center gap-2">
									<input
										type="text"
										value={salesRepNumber}
										onChange={(e) => setSalesRepNumber(e.target.value)}
										placeholder={t('customers.contactRecord.customerCard.salesRepPlaceholder')}
										disabled={saving}
										className="flex-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
									/>
									<button
										type="button"
										onClick={handleSaveSalesRep}
										disabled={saving}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										<CheckIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={handleCancelSalesRep}
										disabled={saving}
										className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-md shadow-sm hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<span>{salesRepNumber || '—'}</span>
									<button
										type="button"
										onClick={handleEditSalesRep}
										className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
									>
										{t('customers.contactRecord.customerCard.edit')}
									</button>
								</div>
							)}
						</dd>
					</div>
				</div>
			</div>
		</div>
	)
}
